/**
 * Size indicators as glyph OUTLINES.
 *
 * CorelDRAW imports a `<g clip-path>` as a PowerClip, but `<text>` children
 * are not clipped with the curves — they come in as loose grouped text next
 * to the PowerClip. Rendering the label through the same OpenType path
 * pipeline the names/numbers use turns it into ordinary curves that ride
 * inside the panel's PowerClip like everything else.
 */

import * as opentypeNs from 'opentype.js';

/** The subset of an opentype.js Font we rely on. */
export interface GlyphFont {
  getAdvanceWidth(text: string, fontSize: number): number;
  getPath(
    text: string,
    x: number,
    y: number,
    fontSize: number
  ): { toPathData(decimals?: number): string };
}

const round = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Centered label at (x, baseline y): white outline under black fill, same
 * look as sizeLabelMarkup but as paths. Uppercase so no descender crosses the
 * panel edge the baseline sits on.
 */
export function sizeLabelPathMarkup(
  font: GlyphFont,
  x: number,
  y: number,
  text: string,
  fontSize: number,
  strokeWidth: number
): string | null {
  const label = text.toUpperCase();
  if (!label.trim()) return null;
  try {
    const width = font.getAdvanceWidth(label, fontSize);
    const d = font.getPath(label, x - width / 2, y, fontSize).toPathData(3);
    if (!d) return null;
    return (
      `<path d="${d}" fill="none" stroke="#ffffff" stroke-width="${round(strokeWidth)}"` +
      ` stroke-linejoin="round"/>` +
      `<path d="${d}" fill="#000000"/>`
    );
  } catch {
    return null;
  }
}

/**
 * Bundled label face (Roboto Condensed Bold, Apache-2.0, public/fonts/). The
 * labels used to borrow "the first font the design loaded" — an order without
 * names or numbers loads none, so every label silently degraded to plain
 * <text> (which CorelDRAW drops out of the PowerClip) and the edge-following
 * layout never ran. One face for every label also keeps sheets uniform.
 */
export const LABEL_FONT_PATH = 'fonts/production-label.ttf';

let labelFontPromise: Promise<GlyphFont | null> | null = null;

function labelFontUrl(): string {
  const base = String((import.meta as any).env?.BASE_URL ?? '/');
  return base.replace(/\/+$/, '') + '/' + LABEL_FONT_PATH;
}

/**
 * The label font: the bundled face, else (bundle unreachable) the first font
 * the design loaded, else null (callers fall back to <text>).
 */
export function loadLabelFont(
  fontCache?: Map<string, Promise<unknown>>
): Promise<GlyphFont | null> {
  if (!labelFontPromise) {
    const load = (opentypeNs as any).load ?? (opentypeNs as any).default?.load;
    labelFontPromise = new Promise<GlyphFont | null>((resolve) => {
      try {
        load(labelFontUrl(), (err: Error | null, font: GlyphFont) =>
          resolve(err || !font || typeof font.getPath !== 'function' ? null : font)
        );
      } catch {
        resolve(null);
      }
    });
  }
  return labelFontPromise.then((font) => font ?? (fontCache ? firstLoadedFont(fontCache) : null));
}

/** Test seam: forget the cached bundled font (or inject one). */
export function resetLabelFont(font?: GlyphFont | null): void {
  labelFontPromise = font === undefined ? null : Promise.resolve(font);
}

/** First resolved font in the engine's font cache (fonts load during compose). */
export async function firstLoadedFont(
  fontCache: Map<string, Promise<unknown>>
): Promise<GlyphFont | null> {
  for (const pending of fontCache.values()) {
    const font = (await pending.catch(() => null)) as GlyphFont | null;
    if (font && typeof font.getPath === 'function') return font;
  }
  return null;
}
